import { useState } from "react";
import { ArrowUpRight, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { submitContact } from "@/lib/api";

const interests = ["1-to-1 Coaching", "Outdoor Adventure Day", "Keynote / Workshop", "Corporate Programme", "RX4 RESET Workshop", "Something else"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact({ ...form, interest: form.interest || undefined });
      toast.success("Thank you. Your message is on its way and Dave will be in touch soon.");
      setForm({ name: "", email: "", interest: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="section-shell pt-8 md:pt-12" data-testid="contact-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline-label mb-5">Contact</p>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-bone leading-[1.05]" data-testid="contact-headline">
                Let's have<br />a <span className="text-brand">chat.</span>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-slate-400 leading-relaxed max-w-md">
                Tell us what you are looking for and where you need support. We can explain the packages in more depth, or tailor a solution specifically for you.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-12 space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-detail-location">
                  <MapPin size={20} className="text-brand mt-1" strokeWidth={1.75} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">RX4 Lakes Limited</p>
                    <p className="text-bone font-medium mt-1">Cleator Moor, Cumbria, UK</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-detail-email">
                  <Mail size={20} className="text-brand mt-1" strokeWidth={1.75} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</p>
                    <a href="mailto:rx4lakes@outlook.com" className="text-brand hover:text-brand-hover transition-colors duration-300 mt-1 inline-block font-medium">
                      rx4lakes@outlook.com
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-10 bg-ink-soft border border-white/10 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-name" className="overline-label block mb-2">Name</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your full name"
                    className="input-dark"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="overline-label block mb-2">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className="input-dark"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-interest" className="overline-label block mb-2">I am interested in</label>
                <select
                  id="contact-interest"
                  data-testid="contact-interest-select"
                  value={form.interest}
                  onChange={set("interest")}
                  className="input-dark bg-ink-soft appearance-none cursor-pointer"
                >
                  <option value="" className="bg-ink">Select an option (optional)</option>
                  {interests.map((i) => (
                    <option key={i} value={i} className="bg-ink">{i}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="overline-label block mb-2">Message</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us where you are, and where you would like to be..."
                  className="input-dark resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="btn-primary group disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Message"}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
